import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Loader2, Megaphone } from 'lucide-react';
import { ImageUpload, API_URL } from './shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

/**
 * Floating Popups admin tab.
 *
 * Manages the closable promo ad that appears on the homepage. Two common
 * setups:
 *   - audience="anonymous": show "Sign up free + free Discover Scuba" to
 *     visitors who haven't logged in.
 *   - audience="user": show "Upgrade to Club/Pro" to existing users.
 * Mark the previous campaign inactive (or delete it) before activating a new
 * one for the same audience — only one ad shows at a time per audience.
 */

const empty = {
  name: '',
  image_url: '',
  link_url: '',
  audience: 'anonymous',
  active: true,
  dismissal_days: 7,
};

const PopupsTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null | popup object | 'new'
  const [form, setForm] = useState(empty);

  const fetchPopups = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/popups`, { headers });
      setItems(r.data.items || []);
    } catch (_e) {
      toast.error('Failed to load popups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPopups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing('new'); setForm(empty); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.image_url) { toast.error('Please upload a popup image'); return; }
    if (!form.name?.trim()) { toast.error('Please give the popup an internal name'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API_URL}/admin/popups`, form, { headers });
        toast.success('Popup created');
      } else {
        await axios.put(`${API_URL}/admin/popups/${editing.id}`, form, { headers });
        toast.success('Popup updated');
      }
      cancel();
      fetchPopups();
    } catch (_e) {
      toast.error('Failed to save popup');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/popups/${p.id}`, { headers });
      toast.success('Deleted');
      fetchPopups();
    } catch (_e) {
      toast.error('Failed to delete');
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API_URL}/admin/popups/${p.id}`, { active: !p.active }, { headers });
      fetchPopups();
    } catch (_e) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#FF6B35]" /> Floating Popups
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Closable promo image that floats over the homepage. Target it at <strong>anonymous</strong> visitors
            (to push registration) or <strong>logged-in users</strong> (to push membership upgrades). Only the most
            recent active popup per audience is shown.
          </p>
        </div>
        {editing === null && (
          <Button onClick={openNew} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="popup-add-btn">
            <Plus className="w-4 h-4 mr-2" /> New Popup
          </Button>
        )}
      </div>

      {editing !== null && (
        <div className="bg-white rounded-lg border p-6 space-y-5" data-testid="popup-form">
          <h3 className="font-semibold text-[#0A2342]">
            {editing === 'new' ? 'Create Popup' : `Edit: ${editing.name}`}
          </h3>

          <div>
            <Label htmlFor="popup-name">Internal Name</Label>
            <Input
              id="popup-name"
              placeholder="e.g. Free Discover Scuba — anonymous"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
              data-testid="popup-name-input"
            />
            <p className="text-xs text-gray-500 mt-1">Just for you — not shown to visitors.</p>
          </div>

          <div>
            <Label>Popup Image (Photoshop creative)</Label>
            <p className="text-xs text-gray-500 mb-2">PNG with transparency is fine — no white frame is added. Will display up to 480px wide (≈85% width on mobile). Square or portrait works best.</p>
            <div className="w-48">
              <ImageUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} token={token} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="popup-audience">Show to</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger id="popup-audience" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="anonymous">Anonymous visitors (not logged in)</SelectItem>
                  <SelectItem value="user">Logged-in users only</SelectItem>
                  <SelectItem value="all">Everyone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="popup-dismiss">Hide after dismiss (days)</Label>
              <Input
                id="popup-dismiss"
                type="number"
                min={0}
                max={365}
                value={form.dismissal_days}
                onChange={(e) => setForm({ ...form, dismissal_days: parseInt(e.target.value || '0', 10) })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">0 = never re-shows; 7 = re-shows after a week.</p>
            </div>
          </div>

          <div>
            <Label htmlFor="popup-link">Link URL (optional)</Label>
            <Input
              id="popup-link"
              placeholder="/auth?mode=register  OR  https://justdive.co.za/membership"
              value={form.link_url}
              onChange={(e) => setForm({ ...form, link_url: e.target.value })}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Clicking the popup sends visitors here. Leave blank for an image-only badge.</p>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="popup-active"
              type="checkbox"
              checked={!!form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="popup-active" className="cursor-pointer">Active (visible to matching visitors)</Label>
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Button onClick={save} disabled={saving} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="popup-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing === 'new' ? 'Create' : 'Save Changes'}
            </Button>
            <Button variant="ghost" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-gray-500 italic">No popups yet — create your first one above.</p>
        )}
        {items.map((p) => {
          const imp = p.impressions || 0;
          const clk = p.clicks || 0;
          const dis = p.dismissals || 0;
          const ctr = imp ? ((clk / imp) * 100).toFixed(1) : '0.0';
          return (
          <div key={p.id} className={`flex items-center gap-4 p-4 bg-white border rounded-lg ${!p.active ? 'opacity-60' : ''}`} data-testid={`popup-row-${p.id}`}>
            <img src={p.image_url} alt={p.name} className="w-16 h-16 object-cover rounded-md bg-gray-100" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{p.name}</p>
              <p className="text-xs text-gray-500 truncate">
                <span className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-[10px] mr-2 uppercase font-semibold">{p.audience}</span>
                {p.link_url || <span className="italic">no link</span>}
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px] text-gray-600">
                <span><span className="font-semibold text-[#0A2342]">{imp.toLocaleString()}</span> impressions</span>
                <span><span className="font-semibold text-[#0A2342]">{clk.toLocaleString()}</span> clicks</span>
                <span className={`px-1.5 py-0.5 rounded-sm ${imp && clk ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'} font-semibold`}>
                  CTR {ctr}%
                </span>
                <span className="text-gray-400">{dis.toLocaleString()} dismissed</span>
              </div>
            </div>
            <button
              onClick={() => toggleActive(p)}
              className={`text-xs font-semibold px-3 py-1 rounded-full ${p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}
              data-testid={`popup-toggle-${p.id}`}
            >
              {p.active ? 'Active' : 'Inactive'}
            </button>
            <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
            <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`popup-delete-${p.id}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default PopupsTab;
